import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { businessInfo } from '../mock/data';

const TermsConditions = () => {
  const lastUpdated = "May 26, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#152238] text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-[#A8B5C8] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Terms & Conditions</h1>
          <p className="text-[#A8B5C8] mt-2">Last updated: {lastUpdated}</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose max-w-none space-y-8 text-gray-800">

          <section>
            <p className="text-lg leading-relaxed">
              These Terms & Conditions ("Terms") govern your use of the {businessInfo.name} LLC website and services, including our SMS/text message program. By using our services, you agree to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">1. About Us</h2>
            <p>
              {businessInfo.name} LLC is a licensed insurance agency specializing in ACA marketplace and private off-market health insurance plans for individuals, families, self-employed professionals, and small businesses. Services are provided by {businessInfo.agentName}, a licensed insurance agent (NPN {businessInfo.npn}) operating in multiple states.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">2. Services</h2>
            <p>
              We provide plan comparisons, enrollment assistance, and ongoing policy support. Plan availability varies by carrier, county, and eligibility. We do not provide legal, tax, or medical advice.
            </p>
          </section>

          <section className="bg-[#FDF6E3] border border-[#F0E5C8] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#152238] mb-4">3. SMS / Text Messaging Program</h2>

            <h3 className="text-lg font-semibold text-[#152238] mt-4 mb-2">Types of messages you may receive</h3>
            <p className="mb-2">By opting in to our SMS program, you may receive messages related to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Appointment reminders and scheduling confirmations</li>
              <li>Quote follow-ups and enrollment status updates</li>
              <li>Policy and account notifications (e.g., payment reminders, ID card updates, PCP selection)</li>
              <li>Customer service responses related to your inquiries</li>
              <li>Open enrollment and renewal reminders</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#152238] mt-6 mb-2">Program terms</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Messaging frequency may vary.</strong></li>
              <li><strong>Message and data rates may apply.</strong></li>
              <li><strong>To opt out at any time, reply STOP</strong> to any message from us.</li>
              <li><strong>For assistance, reply HELP</strong> or visit our website at <a href="https://summitlifehealth.com" className="text-[#3B5275] underline">summitlifehealth.com</a>.</li>
              <li>Visit our <Link to="/privacy-policy" className="text-[#3B5275] underline">Privacy Policy</Link> for details on how your information is handled, and these Terms for the full Terms of Service.</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#152238] mt-6 mb-2">Example opt-in language</h3>
            <p className="italic bg-white border border-gray-200 rounded p-4">
              "By providing your mobile number, you agree to receive text messages from {businessInfo.name} LLC related to your inquiry, quote, enrollment, and policy service. Messaging frequency may vary. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help. See our Privacy Policy and Terms & Conditions."
            </p>

            <h3 className="text-lg font-semibold text-[#152238] mt-6 mb-2">Supported carriers</h3>
            <p>
              Our SMS program is compatible with all major U.S. mobile carriers. Carriers are not liable for delayed or undelivered messages.
            </p>

            <h3 className="text-lg font-semibold text-[#152238] mt-6 mb-2">Consent and third parties</h3>
            <p>
              <strong>SMS consent is not shared with third parties or affiliates.</strong> Your mobile number and opt-in data are used only to communicate with you about your inquiries and services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">4. No Insurance Guarantee</h2>
            <p>
              Submitting information through our website or SMS does not guarantee insurance coverage. Coverage is subject to carrier underwriting, eligibility rules, and marketplace enrollment windows.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">5. Accuracy of Information</h2>
            <p>
              You agree to provide accurate and current information when requesting quotes, enrolling in coverage, or communicating with us. Inaccurate information may affect eligibility, premiums, or coverage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">6. Intellectual Property</h2>
            <p>
              All content on this website, including text, logos, and graphics, is owned by {businessInfo.name} LLC or its licensors (such as carrier logos used with permission for referential purposes). You may not copy or reuse content without written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">7. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, {businessInfo.name} LLC is not liable for indirect, incidental, or consequential damages arising from your use of the website or SMS program. Nothing in these Terms limits any liability that cannot be excluded under applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">8. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our services constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">9. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of Florida, without regard to its conflict of laws principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">10. Contact Us</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <p><strong>{businessInfo.name} LLC</strong></p>
              <p>Attn: {businessInfo.agentName}, {businessInfo.agentTitle}</p>
              <p>{businessInfo.location}</p>
              <p>Phone: <a href={`tel:${businessInfo.phone}`} className="text-[#3B5275] underline">{businessInfo.phone}</a></p>
              <p>Email: <a href={`mailto:${businessInfo.email}`} className="text-[#3B5275] underline break-all">{businessInfo.email}</a></p>
              <p>NPN: {businessInfo.npn}</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
